// src/components/resources/guides/GuidesLayout.tsx

'use client'

import { motion } from 'motion/react'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import useScroll from '~/lib/use-scroll'

interface GuidesLayoutProps {
  children: React.ReactNode
  title?: string
  description?: string
}

export function GuidesLayout({ children, title = 'Marketing Guides', description = 'In-depth guides and resources to help you master digital marketing and creative optimization.' }: GuidesLayoutProps): JSX.Element {
  const scrolled = useScroll(50)

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div className={`absolute inset-0 pointer-events-none transition-all duration-300 ease-in-out ${
          scrolled
            ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
            : 'bg-gradient-to-b from-white/60 via-white/30 to-transparent dark:from-gray-950/60 dark:via-gray-950/30 dark:to-transparent'
        }`} />

        {/* Space for Navbar */}
        <div className="h-[80px]" />
        
        {/* Breadcrumbs Container */}
        <div className="relative">
          <Breadcrumbs />
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative pt-[148px]">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]" />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/90 to-white dark:from-gray-950 dark:via-gray-950/90 dark:to-gray-950" />

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          {/* Title and Description */}
          <div className="text-center">
            <motion.h1 
              className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {title}
            </motion.h1>
            <motion.p 
              className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-400"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {description}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-24 lg:px-8">
        {children}
      </div>
    </div>
  )
} 
